import { useEffect, useState } from "react";
import { QueryClient } from "react-query";
import { LicenseInfo } from "@mui/x-license";
import moment, { Duration } from "moment";
import momenttz from "moment-timezone";

import { createImmiClient, createImmiProvider } from "@imminently/react/store/query";
import type { ImmiClientOptions, Client, DataProvider, Resource } from "@imminently/react/store/query";
import { getConfigValues } from "@imminently/react/context/config";
import { PatientEncounter, Task, EncounterStatus } from "@/resources/types";
import { INotification, pushNotification } from "@imminently/react/store/notifications";
import { useDispatch } from "react-redux";
import { capitalize } from "lodash";
import { AzureConfig, azureAuthBearerHeader, azureProvider } from "@/AzureProvider";
import { CreateResourceServiceConfig } from "@/query//types";
import { createResourceService } from "@/query//createResourceService";
import { getStoredActAsToken } from "@/contexts/actAsStorage";

const ACT_AS_UNAUTHORISED_MESSAGE = "Act as unauthorised";

function getActAsHeader(): Record<string, string> {
  const token = getStoredActAsToken();
  return token ? { "X-ACT-AS": token } : {};
}

function isEncountersRequest(url: string): boolean {
  const path = url.replace(/^\//, "").trim();
  const firstSegment = path.split("/")[0];
  return firstSegment === "encounters";
}

export function isActAsUnauthorisedError(err: unknown): boolean {
  const e = err as { response?: { data?: { message?: string } }; data?: { message?: string } };
  const message = e?.response?.data?.message ?? e?.data?.message;
  return message === ACT_AS_UNAUTHORISED_MESSAGE;
}

let onActAsUnauthorised: (() => void) | null = null;

/** Register a handler to be called when an API response indicates act-as unauthorised (message "Act as unauthorised"). Used by ActAsContext. */
export const registerActAsUnauthorisedHandler = (handler: (() => void) | null) => {
  onActAsUnauthorised = handler;
};

function withActAsHeadersIfEncounters<T extends Record<string, unknown>>(url: string, options: T | undefined): T {
  if (!isEncountersRequest(url)) return (options ?? {}) as T;
  const actAs = getActAsHeader();
  if (Object.keys(actAs).length === 0) return (options ?? {}) as T;
  const base = (options ?? {}) as T & { headers?: Record<string, string> };
  const existingHeaders = (base.headers && typeof base.headers === "object" && !(base.headers instanceof Headers))
    ? { ...base.headers }
    : {};
  // Let explicitly provided headers win (used for one-off scoped act-as).
  return { ...base, headers: { ...actAs, ...existingHeaders } } as T;
}

function wrapClientForActAs<C extends Client>(client: C): C {
  const wrap = <T>(url: string, fn: () => Promise<T>): Promise<T> =>
    fn().catch((err) => {
      if (isEncountersRequest(url) && isActAsUnauthorisedError(err)) {
        onActAsUnauthorised?.();
      }
      throw err;
    });

  return {
    ...client,
    get: (url: string, ...args: unknown[]) =>
      wrap(url, () => {
        const options = withActAsHeadersIfEncounters(url, args[0] as Record<string, unknown> | undefined);
        return (client.get as (u: string, o?: Record<string, unknown>) => Promise<unknown>)(url, options);
      }),
    post: (url: string, ...args: unknown[]) =>
      wrap(url, () => {
        const body = args[0];
        const options = withActAsHeadersIfEncounters(url, args[1] as Record<string, unknown> | undefined);
        return (client.post as (u: string, b: unknown, o?: Record<string, unknown>) => Promise<unknown>)(url, body, options);
      }),
    patch: (url: string, ...args: unknown[]) =>
      wrap(url, () => {
        const body = args[0];
        const options = withActAsHeadersIfEncounters(url, args[1] as Record<string, unknown> | undefined);
        return (client.patch as (u: string, b: unknown, o?: Record<string, unknown>) => Promise<unknown>)(url, body, options);
      }),
    delete: (url: string, ...args: unknown[]) =>
      wrap(url, () => {
        const options = withActAsHeadersIfEncounters(url, args[0] as Record<string, unknown> | undefined);
        return (client.delete as (u: string, o?: Record<string, unknown>) => Promise<unknown>)(url, options);
      }),
  } as C;
}

// TODO for some reason defining this in @imminently/react.context.config causes some weird bundle error
// NOTE this ONLY provides values in dev, it CANNOT be relied on in production
export const viteProvider = () => {
  if (import.meta.env.DEV) {
    return Object.keys(import.meta.env).reduce((acc, key) => {
      if (key.startsWith("VITE_")) {
        const newKey = key.replace("VITE_", "");
        acc[newKey] = import.meta.env[key];
      }

      return acc;
    }, {} as Record<string, string>);
  }

  return undefined;
};

const loadConfig = () => {
  try {
    const raw = azureProvider(viteProvider)();
    return getConfigValues(raw) as AzureConfig;
  } catch (err) {
    window.location.href = "/config-error.html";
    throw err; // throw so TS goes away
  }
};

export const global = (function () {
  const config = loadConfig();
  const { muiLicense } = config.app;
  if (muiLicense) {
    LicenseInfo.setLicenseKey(muiLicense);
  } else {
    console.error("No muiLicense found in config, using unregistered version.");
  }

  const options: ImmiClientOptions = {
    host: config.api ?? "",
    path: "",
    getAuthHeaders: () => ({
      ...azureAuthBearerHeader(config.auth),
    }),
  };

  const rawClient: Client = createImmiClient(options);
  const client: Client = wrapClientForActAs(rawClient);
  const dataProvider: DataProvider = createImmiProvider(client);
  const queryClient = new QueryClient();

  return {
    client,
    dataProvider,
    queryClient,
    config
  };
})();

type ResourceConfig = Omit<CreateResourceServiceConfig, "resource" | "client" | "dataProvider" | "queryClient">;

/**
 * A utility function to create a resource service with the global client, dataProvider, and queryClient.
 * @param resource The resource name
 * @param config Optional additional config parameters
 */
export const createAppResourceService = <T extends Resource>(resource: string, config: ResourceConfig = {}) =>
  createResourceService<T>({
    resource,
    client: global.client,
    dataProvider: global.dataProvider,
    queryClient: global.queryClient,
    ...config,
  });

export default global;

export const uri = (path: string) =>
  `${window.location.protocol}//${window.location.host}${path}`;

export const asArray = <T = any>(value: T | T[]) => (Array.isArray(value) ? value : value === undefined ? [] : [value]) as T[];

/**
 * Converts a date string to a standard format of dd/mm/yyyy.
 * Supports both dd-mm-yyyy and yyyy-mm-dd formats.
 *
 * @param date The date to convert
 * @param delimeter The delimeter used in the date string
 */
export const normaliseDate = (date: string, delimeter = "-") => {
  const [day] = date.split(delimeter) as [string, string, string];

  if (day.length === 4) {
    // its actually in international
    return moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
  } else {
    // assume australian
    return moment(date, "DD-MM-YYYY").format("DD/MM/YYYY");
  }
};

/**
 * Calculates someones age based on their date of birth.
 * @param dob Date of birth - format dd-mm-yyyy
 */
export const calculateAgeMoment = (dob: string) => {
  const today = moment();
  const birthDate = moment(normaliseDate(dob), "DD/MM/YYYY");
  const age = today.diff(birthDate, "years");
  return age;
};

export const formatDoB = (dob: string | undefined) => {
  if (dob === undefined) return "Unknown";

  // When and why is this sometimes a value?
  if (dob === "Invalid date") return dob;

  return normaliseDate(dob);
};

export const makeDOBString = (dob: string) =>
  `${formatDoB(dob)} (${calculateAgeMoment(dob)})`;

/**
 * Calculates someones age based on their date of birth.
 *
 * **NOTE** does not support dd-mm-yyyy format, use `calculateAgeMoment` instead.
 * @param dob Date of birth - must be supported by Date.parse()
 */
export const calculateAge = (dob: string) => {
  const today = new Date();
  const birthDate = new Date(dob);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/** Utility to get the scrollLeft value of an element by className */
export const useScrollLeft = (className: string) => {
  const [scrollLeft, setScrollLeft] = useState(0);

  // need to use intervals to check for the element because it is not available on first render
  useEffect(() => {
    let element = document.querySelector(`.${className}`);
    let intervalId: number | NodeJS.Timeout = -1;

    const handleScroll = () => {
      setScrollLeft(element?.scrollLeft ?? 0);
    };

    const checkElement = () => {
      if (element) {
        element.addEventListener("scroll", handleScroll);
        clearInterval(intervalId);
      }
      element = document.querySelector(`.${className}`);
    };

    intervalId = setInterval(() => {
      requestAnimationFrame(checkElement);
    }, 10);

    return () => {
      clearInterval(intervalId);
      if (element) {
        element.removeEventListener("scroll", handleScroll);
      }
    };
  }, [className]);

  return scrollLeft;
};

type ScrollInfo = Pick<Element, "scrollLeft" | "scrollTop" | "scrollHeight" | "scrollWidth" | "clientHeight" | "clientWidth">;
interface ScrollState extends ScrollInfo {
  horizontalStart: boolean;
  horizontalEnd: boolean;
  verticalStart: boolean;
  verticalEnd: boolean;
}

/** Utility to get all scroll values of an element by className */
export const useScroll = (className: string) => {
  const [scroll, setScroll] = useState<ScrollState | null>(null);

  // need to use intervals to check for the element because it is not available on first render
  useEffect(() => {
    let element = document.querySelector(`.${className}`);
    let intervalId: number | NodeJS.Timeout = -1;

    const handleScroll = () => {
      if (!element) return;
      const { scrollLeft, scrollTop, scrollWidth, scrollHeight, clientWidth, clientHeight } = element;
      setScroll({
        scrollLeft,
        scrollTop,
        scrollHeight,
        scrollWidth,
        clientHeight,
        clientWidth,
        horizontalStart: scrollLeft === 0,
        horizontalEnd: scrollLeft + clientWidth === scrollWidth,
        verticalStart: scrollTop === 0,
        verticalEnd: scrollTop + clientHeight === scrollHeight
      });
    };

    const checkElement = () => {
      if (element) {
        element.addEventListener("scroll", handleScroll);
        clearInterval(intervalId);
      }
      element = document.querySelector(`.${className}`);
    };

    intervalId = setInterval(() => {
      requestAnimationFrame(checkElement);
    }, 10);

    return () => {
      clearInterval(intervalId);
      if (element) {
        element.removeEventListener("scroll", handleScroll);
      }
    };
  }, [className]);

  return scroll;
};

/**
 * Hook to get the current date and time, updated every `interval` milliseconds.
 * @param {number} [interval=1000] Time between updates in milliseconds.
 */
export const useCurrentDateTime = (interval = 1000) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let effectiveInterval = 1000;
    if (Number.isFinite(interval) && interval > 0) {
      effectiveInterval = Math.max(10, interval);
    }

    const timerId = setInterval(() => {
      setNow(new Date());
    }, effectiveInterval);

    return () => {
      clearInterval(timerId);
    };
  }, [interval]);

  return now;
};

export const getInitials = (name: string) => {
  let names = name.trim().split(" ");

  // Limit to first and last name
  names = names.length > 1 ? [names[0], names[names.length - 1]] : names;

  return names.map((n) => n[0].toUpperCase()).join("");
};

// export const getInitialsObsChart = getInitials;

/**
 * The staging environment stores the name in reverse order to all other environments,
 * which is the only reason we need to reverse the initials there. This is a set-up issue
 * rather than a product issue, but we'll account for it here for now.
 */
export const getInitialsObsChart = (name_: string) => {

  // const authScope = import.meta.env.VITE_BACKEND_URL || import.meta.env.BACKEND_URL || "";
  // const isStaging = authScope.includes("apitriermedical-dev");

  let name = name_;
  if (name && name.includes(", ")) {
    const parts = name.split(", ");
    name = parts[1] + " " + parts[0];
  }

  let names = name.trim().split(" ");
  names = names.length > 1 ? [names[0], names[names.length - 1]] : names;

  // if staging, reverse the order of the initials (shouldn't be needed)
  // if (isStaging) {
  //   names = names.reverse();
  // }

  // zoe grayson (18/dec/2023) - "that's right, initials should be first name initial first then surname initial"
  return (names.map((n) => n[0].toUpperCase()).join(""));
};

/**
 * For some environments the name is stored as `Last, First` instead of `First Last`.
 * This function will clean the name to be in the correct format.
 */
export const cleanCreatedNameObsChart = (name_: string) => {
  let name = name_;
  if (name && name.includes(", ")) {
    const parts = name.split(", ");
    name = parts[1] + " " + parts[0];
  }
  return name;
};

/**
 * Given "first" and "last" name, generate full name in the format of:
 * "Last, First"
 * @param capitaliseLast If set to `true`, generate "LAST, First" instead
 */
export const formatName = (
  first: string,
  last: string,
  capitaliseLast?: boolean
) => {
  const fName = first.trim().charAt(0).toUpperCase() + first.trim().slice(1);
  const lName = capitaliseLast
    ? last.trim().toUpperCase()
    : last.trim().charAt(0).toUpperCase() + last.trim().slice(1);

  return `${lName}, ${fName}`;
};

/**
 * Get the sex/gender info from the Encounter. Returns the sex/gender value
 * and whether it was fetched from the Patient or the Delegate.
 */
export const getSex = ({ patient, delegatedData }: PatientEncounter) => ({
  fromPatient: patient?.sex || patient?.gender,
  fromDelegate: delegatedData?.sex || delegatedData?.gender,
});

/**
 * Get the first letter of the sex/gender.
 * TODO: What about Non-Binary and Prefer to Self-Describe?
 */
export const getShortSex = (encounter: PatientEncounter) => {
  const sexInfo = getSex(encounter);
  const sex = sexInfo.fromPatient || sexInfo.fromDelegate;
  return sex ? sex[0].toUpperCase() : "U";
};

export const getDOB = ({ patient, delegatedData }: PatientEncounter) => ({
  fromPatient:
    (patient && patient.dateOfBirth !== "Invalid date" && patient.dateOfBirth) ||
    undefined,
  fromDelegate:
    (delegatedData?.dateOfBirth !== "Invalid date" &&
      delegatedData?.dateOfBirth) ||
    undefined,
});

/** Get the full formatted name from the patient/delegate provided info */
export const getName = ({ patient, delegatedData }: PatientEncounter) => ({
  fromPatient:
    patient?.firstName &&
    patient?.lastName &&
    formatName(patient.firstName, patient.lastName),
  external:
    patient?.external?.firstName &&
    patient?.external?.lastName &&
    formatName(patient.external.firstName, patient.external.lastName),
  fromDelegate:
    delegatedData?.firstName &&
    delegatedData?.lastName &&
    formatName(delegatedData.firstName, delegatedData.lastName),
});

export const getPatientEncounterPhoto = (encounter: PatientEncounter) => {
  const { photos } = encounter;
  return photos?.find((p) => p.type === "patient")?.data ?? null;
};

/**
 * Filter by active tasks and sort tasks in appropriate order. This ensures RAT
 * Consult task is shown first.
 */
export const getActiveTasks = (tasks: Task[]) => {
  return tasks
    .filter((t) => t.status !== "not_requested")
    .sort((a, b) =>
      a.type === "RAT Consult" ? -1 : b.type === "RAT Consult" ? 1 : 0
    );
};

export interface PatientNotification extends INotification {
  patientName: string;
  encounterId: string;
  when: string;
}

export const usePatientNotification = () => {
  const dispatch = useDispatch();
  return (notifcation: PatientNotification) => {
    // default 10 seconds, info type
    dispatch(pushNotification({ duration: 10000, type: "info", ...notifcation }));
  };
};

/**
 * Returns a duration representing the time from now until a given
 * future `time`.
 */
export const getTimeFromNow = (time: string | number | Date) => {
  const now = moment(new Date());
  const target = moment(new Date(time));
  return moment.duration(target.diff(now));
};

const hospitalTimezone = "Australia/Perth";

export const momentHospitalTime = (d?: any) => momenttz(d).tz(hospitalTimezone);

/**
 * Takes what will be a UTC does, and formats it \
 * to be PERTH, Western Australia time
 * @param date Date or string
 */
export const formatUtc2HospitalDate = (date: Date | string, format = "DD MMM - HH:mm") => {

  // moment(date).format("DD MMM - HH:mm"); // don't do this - this will format it to the local time

  if (date) {
    const utcDate = momenttz.utc(date).toDate();
    const perthDate = momenttz(utcDate).tz(hospitalTimezone).format(format);
    return perthDate;
  }

  return "";
};
export const formatUtc2HospitalDateDbTimestampFormat = (date: Date | string) =>
  formatUtc2HospitalDate(date, "YYYY-MM-DDTHH:mm:ss.SSSZ");

/**
 * Returns a duration representing the time from a past given `time` until now.
 */
export const getTimeUntilNow = (time: string | number | Date) => {
  const now = moment(new Date());
  const target = moment(new Date(time));
  return moment.duration(now.diff(target));
};

/**
 * Format a moment duration in a human readable format.
 * Handles negative durations (e.g. overdue) using the same m/h/d/y format.
 */
export const formatDuration = (duration: Duration) => {
  const totalMins = duration.asMinutes();
  const isNegative = totalMins < 0;
  const absDuration = moment.duration(Math.abs(totalMins), "minutes");

  const mins = Math.floor(absDuration.asMinutes());
  const hours = Math.floor(absDuration.asHours());
  const days = Math.floor(absDuration.asDays());
  const weeks = Math.floor(absDuration.asWeeks());
  const months = Math.floor(absDuration.asMonths());
  const years = Math.floor(absDuration.asYears());

  let result: string;
  if (hours < 1) {
    result = `${mins}m`;
  } else if (days < 1) {
    result = `${hours}h ${absDuration.minutes()}m`;
  } else if (months < 1) {
    result = `${days}d ${absDuration.hours()}h`;
  } else if (weeks > 52) {
    result = `${years}y ${months % 12}m`;
  } else {
    result = `${weeks}w`;
  }

  return isNegative ? `-${result}` : result;
};

const getStatusFilterOptionName = (type: EncounterStatus) => {
  switch (type) {
    case "disabled":
      return "Archived";
    case "pendingDischarged":
      return "Pending Discharge";
    case "didnotpresent":
      return "Did not attend";
    default:
      return type;
  }
};

export const getEncounterStatusForDisplaying = (encounter: PatientEncounter) => {
  const { status, distressed } = encounter;
  const statusList = asArray(status);
  const tooltip = statusList.map(capitalize).join(", ");
  const isArchived = statusList[0] === "disabled";
  const statusToDisplay = getStatusFilterOptionName(statusList[0]);
  const displayStatus = distressed ? "Distressed" : (
    isArchived ? "Archived" : statusToDisplay
  );

  return {
    statusList,
    tooltip,
    displayStatus,
  };
};